import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { PAGE_REGISTRY } from '../config/pagePermissions';
import { Users, Plus, Trash2, X, UserCheck, UserX, Shield, ShieldCheck, ShieldOff, Building2, CreditCard as Edit, Ban, CircleCheck as CheckCircle, Clock, TriangleAlert as AlertTriangle } from 'lucide-react';
import { hashPassword } from '../lib/auth';
import { getAdminId } from '../utils/getAdminId';
import { useB2BBuyers } from '../hooks/useB2BBuyers';
import type { B2BBuyerFormData, BuyerAccountStatus } from '../types/b2b';

interface ManagedUser {
  id: string;
  email: string;
  full_name: string;
  role: 'admin' | 'worker' | 'viewer';
  admin_id: string | null;
  is_active: boolean;
  allowed_pages: string[];
  created_at: string;
}

const EMPTY_BUYER_FORM: B2BBuyerFormData = {
  email: '', password: '', legal_entity_name: '', trade_name: '',
  vat_number: '', company_registration_number: '',
  billing_address: '', billing_city: '', billing_postal_code: '', billing_country: '',
  shipping_address: '', shipping_city: '', shipping_postal_code: '', shipping_country: '',
  contact_person_name: '', contact_person_role: '', phone: '', email_contact: '',
  alternative_contact_name: '', alternative_contact_email: '', alternative_contact_phone: '',
  preferred_language: 'en', preferred_currency: 'EUR', payment_terms: '',
  credit_limit: 0, account_discount: 1, vat_percentage: 21, internal_notes: '', account_status: 'active' as BuyerAccountStatus,
};

export const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'team' | 'b2b'>('team');
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [permissionsUserId, setPermissionsUserId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'worker' as 'worker' | 'viewer',
    allowed_pages: [] as string[],
  });

  // B2B Buyer state
  const { buyers, loading: buyersLoading, createBuyer, updateBuyer, updateBuyerStatus, deleteBuyer } = useB2BBuyers();
  const [showBuyerModal, setShowBuyerModal] = useState(false);
  const [editingBuyerId, setEditingBuyerId] = useState<string | null>(null);
  const [buyerForm, setBuyerForm] = useState<B2BBuyerFormData>({ ...EMPTY_BUYER_FORM });
  const [buyerSaving, setBuyerSaving] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    if (!currentUser) return;
    const adminId = getAdminId(currentUser);
    if (!adminId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, email, full_name, role, admin_id, is_active, allowed_pages, created_at')
        .eq('admin_id', adminId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers((data ?? []).map(u => ({ ...u, allowed_pages: u.allowed_pages ?? [] })));
    } catch (err) {
      console.error('Error loading users:', err);
      alert('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.password || !formData.full_name) {
      alert(t('users.fillRequired'));
      return;
    }
    if (formData.password.length < 6) {
      alert(t('users.passwordLength'));
      return;
    }
    const adminId = getAdminId(currentUser);
    if (!adminId) {
      alert(t('users.createFailed'));
      return;
    }

    try {
      setLoading(true);
      const passwordHash = await hashPassword(formData.password);

      const { error } = await supabase
        .from('users')
        .insert({
          email: formData.email.trim().toLowerCase(),
          password_hash: passwordHash,
          full_name: formData.full_name,
          role: formData.role,
          admin_id: adminId,
          is_active: true,
          allowed_pages: formData.allowed_pages,
        });

      if (error) throw error;

      alert(t('users.createdSuccess'));
      setShowCreateModal(false);
      setFormData({ email: '', password: '', full_name: '', role: 'worker', allowed_pages: [] });
      await loadUsers();
    } catch (err: any) {
      console.error('Error creating user:', err);
      if (err.code === '23505') {
        alert(t('users.emailExists'));
      } else {
        alert(t('users.createFailed') + ': ' + (err.message || 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (userId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('users')
        .update({ is_active: !isActive })
        .eq('id', userId);
      if (error) throw error;
      alert(t('users.toggleSuccess'));
      await loadUsers();
    } catch (err) {
      console.error('Error updating user status:', err);
      alert(t('users.toggleFailed'));
    }
  };

  const handleTogglePageAccess = useCallback(async (userId: string, pageKey: string, currentPages: string[]) => {
    const has = currentPages.includes(pageKey);
    const updated = has ? currentPages.filter(k => k !== pageKey) : [...currentPages, pageKey];

    setUsers(prev => prev.map(u => u.id === userId ? { ...u, allowed_pages: updated } : u));

    try {
      const { error } = await supabase
        .from('users')
        .update({ allowed_pages: updated })
        .eq('id', userId);
      if (error) throw error;
    } catch (err) {
      console.error('Error updating page access:', err);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, allowed_pages: currentPages } : u));
      alert('Failed to update page access');
    }
  }, []);

  const handleGrantAll = useCallback(async (userId: string) => {
    const allKeys = PAGE_REGISTRY.map(p => p.key);
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, allowed_pages: allKeys } : u));
    try {
      const { error } = await supabase.from('users').update({ allowed_pages: allKeys }).eq('id', userId);
      if (error) throw error;
    } catch (err) {
      console.error('Error granting all access:', err);
      await loadUsers();
    }
  }, []);

  const handleRevokeAll = useCallback(async (userId: string) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, allowed_pages: [] } : u));
    try {
      const { error } = await supabase.from('users').update({ allowed_pages: [] }).eq('id', userId);
      if (error) throw error;
    } catch (err) {
      console.error('Error revoking all access:', err);
      await loadUsers();
    }
  }, []);

  const handleDeleteUser = async (userId: string) => {
    if (!confirm(t('users.confirmDelete'))) return;
    try {
      if (!currentUser?.id || !currentUser?.role) throw new Error('User not authenticated');
      const { data, error } = await supabase.rpc('delete_user_secure', {
        p_caller_id: currentUser.id,
        p_caller_role: currentUser.role,
        p_user_id: userId,
      });
      if (error) throw error;
      if (data && !data.success) throw new Error(data.error || 'Failed to delete user');
      alert(t('users.deleteSuccess'));
      await loadUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      alert(`${t('users.deleteFailed')}: ${errorMessage}`);
    }
  };

  const permissionsUser = permissionsUserId ? users.find(u => u.id === permissionsUserId) : null;

  const handleCreateBuyer = async (e: React.FormEvent) => {
    e.preventDefault();
    const isEditMode = !!editingBuyerId;
    const hasPasswordInput = buyerForm.password.trim().length > 0;

    if (
      !buyerForm.legal_entity_name ||
      !buyerForm.trade_name ||
      !buyerForm.contact_person_name ||
      (!isEditMode && (!buyerForm.email || !hasPasswordInput))
    ) {
      alert(isEditMode ? 'Please fill in required profile fields.' : 'Please fill in all required fields.');
      return;
    }
    if (hasPasswordInput && buyerForm.password.length < 6) {
      alert('Password must be at least 6 characters.');
      return;
    }
    setBuyerSaving(true);
    const result = editingBuyerId
      ? await updateBuyer(editingBuyerId, {
          legal_entity_name: buyerForm.legal_entity_name,
          trade_name: buyerForm.trade_name,
          vat_number: buyerForm.vat_number || undefined,
          company_registration_number: buyerForm.company_registration_number || undefined,
          billing_address: buyerForm.billing_address || undefined,
          billing_city: buyerForm.billing_city || undefined,
          billing_postal_code: buyerForm.billing_postal_code || undefined,
          billing_country: buyerForm.billing_country || undefined,
          shipping_address: buyerForm.shipping_address || undefined,
          shipping_city: buyerForm.shipping_city || undefined,
          shipping_postal_code: buyerForm.shipping_postal_code || undefined,
          shipping_country: buyerForm.shipping_country || undefined,
          contact_person_name: buyerForm.contact_person_name,
          contact_person_role: buyerForm.contact_person_role || undefined,
          phone: buyerForm.phone || undefined,
          email_contact: buyerForm.email_contact || undefined,
          alternative_contact_name: buyerForm.alternative_contact_name || undefined,
          alternative_contact_email: buyerForm.alternative_contact_email || undefined,
          alternative_contact_phone: buyerForm.alternative_contact_phone || undefined,
          preferred_language: buyerForm.preferred_language,
          preferred_currency: buyerForm.preferred_currency,
          payment_terms: buyerForm.payment_terms || undefined,
          credit_limit: buyerForm.credit_limit,
          account_discount: buyerForm.account_discount,
          vat_percentage: buyerForm.vat_percentage ?? 21,
          internal_notes: buyerForm.internal_notes || undefined,
          account_status: buyerForm.account_status,
          ...(hasPasswordInput ? { password_hash: await hashPassword(buyerForm.password) } : {}),
        } as any)
      : await createBuyer(buyerForm);
    setBuyerSaving(false);
    if (result.success) {
      alert(editingBuyerId ? 'Buyer updated successfully.' : 'Buyer created successfully.');
      setShowBuyerModal(false);
      setEditingBuyerId(null);
      setBuyerForm({ ...EMPTY_BUYER_FORM });
    } else {
      alert(result.error || 'Failed to save buyer.');
    }
  };

  const openEditBuyer = (buyerId: string) => {
    const b = buyers.find(x => x.id === buyerId);
    if (!b) return;
    setEditingBuyerId(buyerId);
    setBuyerForm({
      email: b.email,
      password: '',
      legal_entity_name: b.legal_entity_name,
      trade_name: b.trade_name,
      vat_number: b.vat_number || '',
      company_registration_number: b.company_registration_number || '',
      billing_address: b.billing_address || '',
      billing_city: b.billing_city || '',
      billing_postal_code: b.billing_postal_code || '',
      billing_country: b.billing_country || '',
      shipping_address: b.shipping_address || '',
      shipping_city: b.shipping_city || '',
      shipping_postal_code: b.shipping_postal_code || '',
      shipping_country: b.shipping_country || '',
      contact_person_name: b.contact_person_name,
      contact_person_role: b.contact_person_role || '',
      phone: b.phone || '',
      email_contact: b.email_contact || '',
      alternative_contact_name: b.alternative_contact_name || '',
      alternative_contact_email: b.alternative_contact_email || '',
      alternative_contact_phone: b.alternative_contact_phone || '',
      preferred_language: b.preferred_language || 'en',
      preferred_currency: b.preferred_currency || 'EUR',
      payment_terms: b.payment_terms || '',
      credit_limit: b.credit_limit || 0,
      account_discount: b.account_discount || 1,
      vat_percentage: b.vat_percentage ?? 21,
      internal_notes: b.internal_notes || '',
      account_status: b.account_status,
    });
    setShowBuyerModal(true);
  };

  const statusColors: Record<string, string> = {
    active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    pending_approval: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    blocked: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    suspended: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  };

  if (currentUser?.role !== 'admin') {
    return (
      <div className="p-6">
        <div className="card p-6 text-center">
          <UserX className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('users.accessDenied')}</h2>
          <p className="text-gray-600 dark:text-gray-400">{t('users.adminOnly')}</p>
        </div>
      </div>
    );
  }

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('users.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400">{t('users.subtitle')}</p>
        </div>
        {activeTab === 'team' ? (
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('users.addUser')}
          </Button>
        ) : (
          <Button onClick={() => { setEditingBuyerId(null); setBuyerForm({ ...EMPTY_BUYER_FORM }); setShowBuyerModal(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add B2B Buyer
          </Button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('team')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'team'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <Users className="h-4 w-4 inline mr-2" />
          Team Members
        </button>
        <button
          onClick={() => setActiveTab('b2b')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'b2b'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <Building2 className="h-4 w-4 inline mr-2" />
          B2B Buyers
          {buyers.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">{buyers.length}</span>
          )}
        </button>
      </div>

      {activeTab === 'team' && (<>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('users.totalUsers')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{users.length}</p>
            </div>
            <Users className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('users.active')}</p>
              <p className="text-2xl font-bold text-green-600">{users.filter(u => u.is_active).length}</p>
            </div>
            <UserCheck className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('users.inactive')}</p>
              <p className="text-2xl font-bold text-red-600">{users.filter(u => !u.is_active).length}</p>
            </div>
            <UserX className="h-8 w-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Users List */}
      {users.length === 0 ? (
        <div className="card p-12 text-center">
          <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{t('users.noUsers')}</h3>
          <p className="text-gray-600 dark:text-gray-400">{t('users.addFirstUser')}</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('users.user')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('users.role')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('users.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Page Access</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('users.created')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('users.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{user.full_name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {user.is_active ? t('users.active') : t('users.inactive')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => setPermissionsUserId(user.id)}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/50"
                    >
                      <Shield className="h-4 w-4" />
                      {user.allowed_pages.length} / {PAGE_REGISTRY.length}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" size="sm" onClick={() => handleToggleActive(user.id, user.is_active)}>
                        {user.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => handleDeleteUser(user.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      </>)}

      {/* B2B Buyers Tab */}
      {activeTab === 'b2b' && (
        <>
          {/* B2B Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Buyers</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{buyers.length}</p>
                </div>
                <Building2 className="h-8 w-8 text-blue-500" />
              </div>
            </div>
            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active</p>
                  <p className="text-2xl font-bold text-green-600">{buyers.filter(b => b.account_status === 'active').length}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </div>
            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{buyers.filter(b => b.account_status === 'pending_approval').length}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-500" />
              </div>
            </div>
            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Blocked</p>
                  <p className="text-2xl font-bold text-red-600">{buyers.filter(b => b.account_status === 'blocked' || b.account_status === 'suspended').length}</p>
                </div>
                <Ban className="h-8 w-8 text-red-500" />
              </div>
            </div>
          </div>

          {/* Buyers List */}
          {buyersLoading && buyers.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
          ) : buyers.length === 0 ? (
            <div className="card p-12 text-center">
              <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No B2B Buyers</h3>
              <p className="text-gray-600 dark:text-gray-400">Add your first business client to get started.</p>
            </div>
          ) : (
            <div className="card overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Discount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {buyers.map((buyer) => (
                    <tr key={buyer.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{buyer.trade_name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{buyer.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">{buyer.contact_person_name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{buyer.phone || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-mono text-gray-700 dark:text-gray-300">{buyer.buyer_code}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-700 dark:text-gray-300">÷ {buyer.account_discount}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[buyer.account_status] || 'bg-gray-100 text-gray-800'}`}>
                          {buyer.account_status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <Button variant="secondary" size="sm" onClick={() => openEditBuyer(buyer.id)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          {buyer.account_status !== 'active' ? (
                            <Button variant="secondary" size="sm" onClick={() => updateBuyerStatus(buyer.id, 'active')}>
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            </Button>
                          ) : (
                            <Button variant="secondary" size="sm" onClick={() => updateBuyerStatus(buyer.id, 'suspended')}>
                              <Ban className="h-4 w-4 text-orange-600" />
                            </Button>
                          )}
                          <Button variant="secondary" size="sm" onClick={() => { if (confirm('Delete this buyer account?')) deleteBuyer(buyer.id); }}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* B2B Buyer Create/Edit Modal */}
      {showBuyerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {editingBuyerId ? 'Edit B2B Buyer' : 'Create B2B Buyer'}
              </h2>
              <button onClick={() => { setShowBuyerModal(false); setEditingBuyerId(null); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateBuyer} className="flex-1 overflow-y-auto">
              <div className="px-6 py-4 space-y-6">
                {/* Account */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">Account</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Email *" type="email" value={buyerForm.email} onChange={e => setBuyerForm({ ...buyerForm, email: e.target.value })} required disabled={!!editingBuyerId} />
                    <Input
                      label={editingBuyerId ? 'New Password (optional)' : 'Password *'}
                      type="password"
                      value={buyerForm.password}
                      onChange={e => setBuyerForm({ ...buyerForm, password: e.target.value })}
                      required={!editingBuyerId}
                      placeholder={editingBuyerId ? 'Leave blank to keep current password' : 'Min 6 characters'}
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Status *</label>
                      <select value={buyerForm.account_status} onChange={e => setBuyerForm({ ...buyerForm, account_status: e.target.value as BuyerAccountStatus })} className="input">
                        <option value="active">Active</option>
                        <option value="pending_approval">Pending Approval</option>
                        <option value="blocked">Blocked</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </div>
                  </div>
                </div>
                {/* Company Details */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">Company Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Legal Entity Name *" value={buyerForm.legal_entity_name} onChange={e => setBuyerForm({ ...buyerForm, legal_entity_name: e.target.value })} required />
                    <Input label="Trade Name / Company Name *" value={buyerForm.trade_name} onChange={e => setBuyerForm({ ...buyerForm, trade_name: e.target.value })} required />
                    <Input label="VAT Number" value={buyerForm.vat_number} onChange={e => setBuyerForm({ ...buyerForm, vat_number: e.target.value })} />
                    <Input label="Company Registration Number" value={buyerForm.company_registration_number} onChange={e => setBuyerForm({ ...buyerForm, company_registration_number: e.target.value })} />
                  </div>
                </div>
                {/* Billing Address */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">Billing Address</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Input label="Address" value={buyerForm.billing_address} onChange={e => setBuyerForm({ ...buyerForm, billing_address: e.target.value })} />
                    </div>
                    <Input label="City" value={buyerForm.billing_city} onChange={e => setBuyerForm({ ...buyerForm, billing_city: e.target.value })} />
                    <Input label="Postal Code" value={buyerForm.billing_postal_code} onChange={e => setBuyerForm({ ...buyerForm, billing_postal_code: e.target.value })} />
                    <Input label="Country" value={buyerForm.billing_country} onChange={e => setBuyerForm({ ...buyerForm, billing_country: e.target.value })} />
                  </div>
                </div>
                {/* Shipping Address */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">Shipping Address</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Input label="Address" value={buyerForm.shipping_address} onChange={e => setBuyerForm({ ...buyerForm, shipping_address: e.target.value })} />
                    </div>
                    <Input label="City" value={buyerForm.shipping_city} onChange={e => setBuyerForm({ ...buyerForm, shipping_city: e.target.value })} />
                    <Input label="Postal Code" value={buyerForm.shipping_postal_code} onChange={e => setBuyerForm({ ...buyerForm, shipping_postal_code: e.target.value })} />
                    <Input label="Country" value={buyerForm.shipping_country} onChange={e => setBuyerForm({ ...buyerForm, shipping_country: e.target.value })} />
                  </div>
                </div>
                {/* Contact */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">Contact Person</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Full Name *" value={buyerForm.contact_person_name} onChange={e => setBuyerForm({ ...buyerForm, contact_person_name: e.target.value })} required />
                    <Input label="Role" value={buyerForm.contact_person_role} onChange={e => setBuyerForm({ ...buyerForm, contact_person_role: e.target.value })} placeholder="e.g. Purchasing Manager" />
                    <Input label="Phone" value={buyerForm.phone} onChange={e => setBuyerForm({ ...buyerForm, phone: e.target.value })} />
                    <Input label="Contact Email" type="email" value={buyerForm.email_contact} onChange={e => setBuyerForm({ ...buyerForm, email_contact: e.target.value })} />
                  </div>
                </div>
                {/* Alternative Contact */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">Alternative Contact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input label="Name" value={buyerForm.alternative_contact_name} onChange={e => setBuyerForm({ ...buyerForm, alternative_contact_name: e.target.value })} />
                    <Input label="Email" type="email" value={buyerForm.alternative_contact_email} onChange={e => setBuyerForm({ ...buyerForm, alternative_contact_email: e.target.value })} />
                    <Input label="Phone" value={buyerForm.alternative_contact_phone} onChange={e => setBuyerForm({ ...buyerForm, alternative_contact_phone: e.target.value })} />
                  </div>
                </div>
                {/* Preferences & Financials */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">Preferences & Financials</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Preferred Language</label>
                      <select value={buyerForm.preferred_language} onChange={e => setBuyerForm({ ...buyerForm, preferred_language: e.target.value })} className="input">
                        <option value="en">English</option>
                        <option value="nl">Nederlands</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Preferred Currency</label>
                      <select value={buyerForm.preferred_currency} onChange={e => setBuyerForm({ ...buyerForm, preferred_currency: e.target.value })} className="input">
                        <option value="EUR">EUR (€)</option>
                        <option value="USD">USD ($)</option>
                        <option value="GBP">GBP (£)</option>
                      </select>
                    </div>
                    <Input label="Payment Terms" value={buyerForm.payment_terms} onChange={e => setBuyerForm({ ...buyerForm, payment_terms: e.target.value })} placeholder="e.g. Net 30" />
                    <Input label="Credit Limit" type="number" value={String(buyerForm.credit_limit)} onChange={e => setBuyerForm({ ...buyerForm, credit_limit: parseFloat(e.target.value) || 0 })} />
                    <div>
                      <Input label="Account Discount (Divisor)" type="number" value={String(buyerForm.account_discount)} onChange={e => setBuyerForm({ ...buyerForm, account_discount: parseFloat(e.target.value) || 1 })} />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Final price = Sell price ÷ this number. Example: €220 ÷ 2.5 = €88
                      </p>
                    </div>
                    <div>
                      <Input label="BTW / VAT %" type="number" min="0" max="100" value={String(buyerForm.vat_percentage)} onChange={e => setBuyerForm({ ...buyerForm, vat_percentage: parseFloat(e.target.value) ?? 21 })} />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        VAT rate applied to invoices. Default is 21% (NL standard).
                      </p>
                    </div>
                  </div>
                </div>
                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Internal Notes</label>
                  <textarea
                    value={buyerForm.internal_notes}
                    onChange={e => setBuyerForm({ ...buyerForm, internal_notes: e.target.value })}
                    rows={3}
                    className="input w-full"
                    placeholder="Notes visible only to admin..."
                  />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
                <Button type="submit" className="flex-1" disabled={buyerSaving}>
                  {buyerSaving ? 'Saving...' : (editingBuyerId ? 'Update Buyer' : 'Create Buyer')}
                </Button>
                <Button type="button" variant="secondary" onClick={() => { setShowBuyerModal(false); setEditingBuyerId(null); }} className="flex-1">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {permissionsUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Page Access Control</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">{permissionsUser.full_name} &middot; {permissionsUser.email}</p>
              </div>
              <button onClick={() => setPermissionsUserId(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
              <button
                onClick={() => handleGrantAll(permissionsUser.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50 transition-colors"
              >
                <ShieldCheck className="h-3.5 w-3.5" />
                Grant All
              </button>
              <button
                onClick={() => handleRevokeAll(permissionsUser.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 transition-colors"
              >
                <ShieldOff className="h-3.5 w-3.5" />
                Revoke All
              </button>
              <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
                {permissionsUser.allowed_pages.length} of {PAGE_REGISTRY.length} enabled
              </span>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PAGE_REGISTRY.map((page) => {
                  const Icon = page.icon;
                  const enabled = permissionsUser.allowed_pages.includes(page.key);
                  return (
                    <button
                      key={page.key}
                      onClick={() => handleTogglePageAccess(permissionsUser.id, page.key, permissionsUser.allowed_pages)}
                      className={`flex items-start gap-3 p-3 rounded-lg border-2 text-left transition-all ${
                        enabled
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-400'
                          : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700/30 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <div className={`mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                        enabled
                          ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-800 dark:text-indigo-300'
                          : 'bg-gray-100 text-gray-400 dark:bg-gray-600 dark:text-gray-400'
                      }`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${
                          enabled ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
                        }`}>
                          {page.label}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{page.description}</p>
                      </div>
                      <div className={`mt-1 flex-shrink-0 w-9 h-5 rounded-full transition-colors relative ${
                        enabled ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-gray-500'
                      }`}>
                        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                          enabled ? 'translate-x-4' : 'translate-x-0.5'
                        }`} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="secondary" className="w-full" onClick={() => setPermissionsUserId(null)}>
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">{t('users.createNew')}</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="flex-1 overflow-y-auto">
              <div className="px-6 py-4 space-y-4">
                <Input
                  label={t('users.fullName') + ' *'}
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                />
                <Input
                  label={t('users.email') + ' *'}
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
                <Input
                  label={t('users.password') + ' *'}
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  placeholder={t('users.minChars')}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('users.role')} *
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as 'worker' | 'viewer' })}
                    className="input"
                    required
                  >
                    <option value="worker">{t('users.worker')}</option>
                    <option value="viewer">{t('users.viewer')}</option>
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Page Access</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, allowed_pages: PAGE_REGISTRY.map(p => p.key) })}
                        className="text-xs text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 font-medium"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, allowed_pages: [] })}
                        className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 font-medium"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {PAGE_REGISTRY.map((page) => {
                      const Icon = page.icon;
                      const checked = formData.allowed_pages.includes(page.key);
                      return (
                        <label
                          key={page.key}
                          className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                            checked
                              ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-400'
                              : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700/30 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const pages = e.target.checked
                                ? [...formData.allowed_pages, page.key]
                                : formData.allowed_pages.filter(k => k !== page.key);
                              setFormData({ ...formData, allowed_pages: pages });
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <Icon className={`h-4 w-4 flex-shrink-0 ${checked ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`} />
                          <span className={`text-sm ${checked ? 'text-gray-900 dark:text-gray-100 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                            {page.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
                <Button type="submit" className="flex-1" disabled={loading}>
                  {loading ? t('users.creating') : t('users.createUser')}
                </Button>
                <Button type="button" variant="secondary" onClick={() => setShowCreateModal(false)} className="flex-1">
                  {t('action.cancel')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
